import { useCallback, useEffect, useState } from "react";
import { getUnifiedToken } from "../utils/unifiedAuth";

const API_BASE =
  import.meta.env.VITE_API_BASE ||
  "https://api.avaaio.com";

export const clearMotionTrackingSession = (): void => {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.setItem(
      "ava_device_motion_look",
      "0"
    );
  } catch {
    // Session storage may be unavailable.
  }
};

type MotionEntitlementState = {
  motionTrackingAllowed: boolean;
  motionEntitlementLoaded: boolean;
  refreshMotionEntitlement: () => Promise<boolean>;
};

export default function useMotionTrackingEntitlement():
  MotionEntitlementState {
  // Fail closed until the live authenticated API confirms access.
  const [
    motionTrackingAllowed,
    setMotionTrackingAllowed,
  ] = useState(false);

  const [
    motionEntitlementLoaded,
    setMotionEntitlementLoaded,
  ] = useState(false);

  const refreshMotionEntitlement =
    useCallback(async (): Promise<boolean> => {
      try {
        const token = getUnifiedToken();

        const response = await fetch(
          `${API_BASE}/api/v1/auth/me`,
          {
            credentials: "include",
            cache: "no-store",
            headers: {
              ...(token
                ? {
                    Authorization:
                      `Bearer ${token}`,
                  }
                : {}),
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Motion entitlement request failed: ${response.status}`
          );
        }

        const payload = await response.json();
        const user = payload?.user ?? payload ?? {};

        // Never derive premium motion access from localStorage.
        // Only the backend-produced boolean unlocks the tracker.
        const allowed =
          user?.motion_tracking_allowed === true;

        setMotionTrackingAllowed(allowed);
        setMotionEntitlementLoaded(true);

        if (!allowed) {
          clearMotionTrackingSession();
        }

        return allowed;
      } catch (error) {
        console.warn(
          "[AVA MOTION] Entitlement verification failed closed.",
          error
        );

        setMotionTrackingAllowed(false);
        setMotionEntitlementLoaded(true);
        clearMotionTrackingSession();

        return false;
      }
    }, []);

  useEffect(() => {
    void refreshMotionEntitlement();

    const refresh = () => {
      void refreshMotionEntitlement();
    };

    window.addEventListener(
      "ava-plan-updated",
      refresh
    );

    window.addEventListener(
      "ava-force-refresh",
      refresh
    );

    return () => {
      window.removeEventListener(
        "ava-plan-updated",
        refresh
      );

      window.removeEventListener(
        "ava-force-refresh",
        refresh
      );
    };
  }, [refreshMotionEntitlement]);

  return {
    motionTrackingAllowed,
    motionEntitlementLoaded,
    refreshMotionEntitlement,
  };
}
